import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import PublicPayments from "components/publicpaymnts";
import InstallationPublicPayments from "components/InstallationPublicPayments";
import RemovalPublicPayments from "components/RemovalPublicPayments";
import {
  fetchPaymentGatewaysCreatedByUserId,
} from "components/proejctsandquoteapicalls";
import {
  fetchNormalizedPublicOrderForSummary,
  isPublicOrderCancelledForSummary,
  type PublicOrderNormalizeKind,
} from "components/orderSummary";
import { getAuthCookie, setSupplierIdContextFromValue } from "utils/authCookies";
import {
  sendRemovalOrderTriggerEmail,
  sendSalesOrderTriggerEmail,
} from "utils/salesOrderTriggerEmailEngine";

const cleanParam = (value?: string | null) =>
  String(value || "")
    .trim()
    .replace(/^"+|"+$/g, "")
    .replace(/^'+|'+$/g, "");

const money = (value?: number) => `$${Number(value || 0).toFixed(2)}`;

function normalizeOrderUserId(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "object" && v !== null && "$oid" in (v as Record<string, unknown>)) {
    return String((v as { $oid?: string }).$oid ?? "").trim();
  }
  if (typeof v === "object" && v !== null && "id" in (v as Record<string, unknown>)) {
    return String((v as { id?: unknown }).id ?? "").trim();
  }
  return String(v).trim();
}

export type PublicOrderPayUiProps = {
  orderKind: PublicOrderNormalizeKind;
  /** Logged-in preview without public token — hide Pay (same as Order_summary private preview). */
  isPrivatePreview?: boolean;
  showPayButton?: boolean;
  companyName?: string;
  /** Fresh public order for totals / payment status (invoice page). */
  onPublicOrderLoaded?: (order: any) => void;
  orderLabel?: string;
  /** When set, parent already fetched the public order — skip duplicate GET. */
  parentFetchesOrder?: boolean;
  /** Order payload from parent (e.g. InvoiceDocument public sales invoice). */
  syncedPublicOrder?: any | null;
};

/**
 * Stripe Pay button + modal — same visibility rules as Order_summary public Pay UI.
 */
export default function PublicOrderPayUi({
  orderKind,
  isPrivatePreview = false,
  showPayButton = true,
  companyName = "",
  onPublicOrderLoaded,
  orderLabel = "",
  parentFetchesOrder = false,
  syncedPublicOrder = null,
}: PublicOrderPayUiProps) {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const queryProjectId = cleanParam(query.get("project_id"));
  const queryAccessToken = cleanParam(query.get("access_token") || query.get("token"));
  const queryPublicUrl = cleanParam(query.get("public_url") || query.get("Public_url"));
  const queryOrderId = cleanParam(query.get("order_id")) || queryProjectId;

  const queryClientSecret = useMemo(() => {
    const raw =
      query.get("client_secret") ??
      query.get("clientSecret") ??
      query.get("payment_intent_client_secret") ??
      "";
    return cleanParam(raw);
  }, [query]);

  const queryPublishableKey = useMemo(() => {
    const raw = query.get("publishable_key") ?? query.get("stripe_publishable_key") ?? "";
    return cleanParam(raw);
  }, [query]);

  const parsedPublicProjectId = useMemo(() => {
    if (!queryPublicUrl) return "";
    try {
      const parsed = queryPublicUrl.startsWith("http")
        ? new URL(queryPublicUrl)
        : new URL(queryPublicUrl, window.location.origin);
      const m = parsed.pathname.match(/\/public\/projects\/([^/?#]+)/i);
      return cleanParam(m?.[1] || "");
    } catch {
      return "";
    }
  }, [queryPublicUrl]);

  const parsedPublicToken = useMemo(() => {
    if (!queryPublicUrl) return "";
    try {
      const parsed = queryPublicUrl.startsWith("http")
        ? new URL(queryPublicUrl)
        : new URL(queryPublicUrl, window.location.origin);
      return cleanParam(parsed.searchParams.get("access_token") || parsed.searchParams.get("token"));
    } catch {
      return "";
    }
  }, [queryPublicUrl]);

  const effectiveProjectId = queryProjectId || parsedPublicProjectId;
  const effectiveAccessToken = queryAccessToken || parsedPublicToken;
  const isPublicPayContext = Boolean(effectiveAccessToken && queryOrderId);

  const isInstallationKind = orderKind === "installation";
  const isRemovalKind = orderKind === "removal";

  const [publicOrder, setPublicOrder] = useState<any | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentSuccessChargedAmount, setPaymentSuccessChargedAmount] = useState<number | null>(null);
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [loadingPaymentUi, setLoadingPaymentUi] = useState(false);
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const autoStripeRetryRef = useRef(false);

  const stripeGatewayUserId = useMemo(() => {
    const fromQuery = cleanParam(query.get("supplier_id"));
    if (fromQuery) return fromQuery;
    const po = publicOrder as Record<string, unknown> | null | undefined;
    const fromOrder = String(
      po?.signage_id ||
        po?.seller_owner_node_id ||
        (po?.created_by && typeof po.created_by === "object"
          ? (po.created_by as { id?: unknown }).id
          : "") ||
        ""
    ).trim();
    if (fromOrder) return fromOrder;
    return queryProjectId;
  }, [query, publicOrder, queryProjectId]);

  const supplierUserIdForPaymentBranding = useMemo(() => {
    const fromQuery = cleanParam(query.get("supplier_id"));
    if (fromQuery) return fromQuery;
    const po = publicOrder as Record<string, unknown> | null | undefined;
    const sid = String(po?.signage_id || po?.seller_owner_node_id || "").trim();
    if (sid) return sid;
    const cb = po?.created_by as { id?: unknown } | undefined;
    if (cb && typeof cb === "object" && cb.id != null) {
      const id = String(cb.id).trim();
      if (id) return id;
    }
    return "";
  }, [query, publicOrder]);

  useEffect(() => {
    const id = String(supplierUserIdForPaymentBranding || "").trim();
    if (!id) return;
    setSupplierIdContextFromValue(id);
  }, [supplierUserIdForPaymentBranding]);

  const paymentTriggerToken = String(effectiveAccessToken || getAuthCookie("sessionToken") || "").trim();

  useEffect(() => {
    if (syncedPublicOrder) {
      setPublicOrder(syncedPublicOrder);
    }
  }, [syncedPublicOrder]);

  useEffect(() => {
    if (parentFetchesOrder || isPrivatePreview || !queryOrderId) return;
    let mounted = true;
    (async () => {
      try {
        const normalized = await fetchNormalizedPublicOrderForSummary(
          isRemovalKind,
          isInstallationKind,
          effectiveProjectId,
          queryOrderId,
          effectiveAccessToken
        );
        if (!mounted) return;
        setPublicOrder(normalized);
        onPublicOrderLoaded?.(normalized);
      } catch {
        if (mounted) setPublicOrder(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [
    parentFetchesOrder,
    isPrivatePreview,
    queryOrderId,
    isRemovalKind,
    isInstallationKind,
    effectiveProjectId,
    queryOrderId,
    effectiveAccessToken,
    onPublicOrderLoaded,
  ]);

  const orderForPayUi = syncedPublicOrder ?? publicOrder;

  const displayGrandTotal = Number(orderForPayUi?.total_amount ?? 0);
  const publicOrderAmountPaid = useMemo(() => {
    const arr = Array.isArray((orderForPayUi as { payment_info?: unknown })?.payment_info)
      ? ((orderForPayUi as { payment_info: { amount_paid?: number | string }[] }).payment_info ?? [])
      : [];
    return arr.reduce((acc: number, p) => acc + Number(p?.amount_paid || 0), 0);
  }, [orderForPayUi]);

  const displayPaymentRemaining = useMemo(() => {
    const r = Number(displayGrandTotal || 0) - Number(publicOrderAmountPaid || 0);
    return Math.max(0, Math.round(r * 100) / 100);
  }, [displayGrandTotal, publicOrderAmountPaid]);

  const paymentStatusLabel = String(orderForPayUi?.payment_status || "").trim();

  const isPublicOrderCancelled = useMemo(
    () =>
      isPublicOrderCancelledForSummary(
        (orderForPayUi as { status?: number | string } | null)?.status,
        isRemovalKind,
        isInstallationKind
      ),
    [orderForPayUi?.status, isInstallationKind, isRemovalKind]
  );

  const isPaymentSettledForPublicPay = useMemo(() => {
    const ps = String(orderForPayUi?.payment_status || paymentStatusLabel || "")
      .trim()
      .toLowerCase();
    return ps === "paid" || ps === "overpaid";
  }, [orderForPayUi?.payment_status, paymentStatusLabel]);

  /** Match Order_summary: visibility does not require access_token (token needed to pay). */
  const effectiveShowPayButton = showPayButton && !isPrivatePreview;
  const hasOrderLoaded = Boolean(orderForPayUi && (orderForPayUi.total_amount != null || orderForPayUi._id || orderForPayUi.id));
  const shouldShowPublicPayUi =
    effectiveShowPayButton &&
    hasOrderLoaded &&
    !isPaymentSettledForPublicPay &&
    !isPublicOrderCancelled &&
    displayPaymentRemaining > 0;

  const paymentCreatedBy = useMemo(() => {
    const po = orderForPayUi as Record<string, unknown> | null | undefined;
    const cb = po?.created_by as { id?: unknown; name?: string } | undefined;
    const idFromCreated = cb && typeof cb === "object" ? normalizeOrderUserId(cb.id) : "";
    const nameFromCreated = cb && typeof cb === "object" ? String(cb.name || "").trim() : "";
    const signageName = String(po?.signage_company_name || "")
      .replace(/^"|"$/g, "")
      .trim();
    return {
      id:
        idFromCreated ||
        normalizeOrderUserId(po?.signage_id) ||
        normalizeOrderUserId(po?.seller_owner_node_id) ||
        String(stripeGatewayUserId || "").trim(),
      name: nameFromCreated || signageName || companyName || "Unknown User",
    };
  }, [orderForPayUi, stripeGatewayUserId, companyName]);

  const paymentPaidBy = useMemo(() => {
    const po = orderForPayUi as Record<string, unknown> | null | undefined;
    const job = po as Record<string, any> | null | undefined;
    const isJob = isInstallationKind || isRemovalKind;

    if (isJob && job) {
      const id =
        normalizeOrderUserId(job?.agent_id) ||
        normalizeOrderUserId(job?.team_member_id) ||
        normalizeOrderUserId(job?.customer_id);
      const name = String(
        job?.user_name ||
          job?.agent_name ||
          job?.team_member_name ||
          [job?.customer_first_name, job?.customer_last_name].filter(Boolean).join(" ") ||
          [job?.billing_address?.first_name, job?.billing_address?.last_name].filter(Boolean).join(" ") ||
          ""
      ).trim();
      if (id || name) {
        return { id, name: name || "Customer" };
      }
    }

    const first = String(po?.customer_first_name || "").trim();
    const last = String(po?.customer_last_name || "").trim();
    const fullName = [first, last].filter(Boolean).join(" ").trim();
    return {
      id: normalizeOrderUserId(po?.customer_id),
      name: fullName || first || last || "",
    };
  }, [orderForPayUi, isInstallationKind, isRemovalKind]);

  const payText = `Pay ${money(displayPaymentRemaining)}`;

  const paymentIntentMeta = useMemo(() => {
    const po = orderForPayUi as Record<string, any> | null | undefined;
    const isJob = isInstallationKind || isRemovalKind;

    if (isJob && po) {
      return {
        order_id: String(queryOrderId || po?.id || po?._id || ""),
        order_number: String(po?.order_number || po?.job_number || ""),
        customer_name: String(
          po?.user_name ||
            po?.agent_name ||
            [po?.customer_first_name, po?.customer_last_name].filter(Boolean).join(" ") ||
            ""
        ).trim(),
        customer_email: String(po?.user_email || po?.customer_email || "").trim(),
        customer_phone: String(po?.customer_phone || po?.user_phone || "").trim(),
      };
    }

    return {
      order_id: String(queryOrderId || po?.id || po?._id || ""),
      order_number: String(po?.order_number || po?.job_number || ""),
      customer_name: [po?.customer_first_name, po?.customer_last_name].filter(Boolean).join(" ").trim(),
      customer_email: String(po?.customer_email || "").trim(),
      customer_phone: String(po?.customer_phone || "").trim(),
    };
  }, [orderForPayUi, queryOrderId, isInstallationKind, isRemovalKind]);

  const openPaymentModal = useCallback(async () => {
    setShowPayModal(true);
    setPaymentInitError(null);
    if (!queryClientSecret && !paymentTriggerToken) {
      setPaymentInitError(
        "This payment link is missing an access token. Open the invoice from the email link you received."
      );
      return;
    }
    if (stripePromise && clientSecret) return;
    try {
      setLoadingPaymentUi(true);

      let publishableKey = String(
        queryPublishableKey ||
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
          process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
          ""
      ).trim();
      if (!publishableKey) {
        const bearer = getAuthCookie("sessionToken") || effectiveAccessToken || "";
        if (bearer) {
          try {
            const gateways = await fetchPaymentGatewaysCreatedByUserId(bearer, stripeGatewayUserId);
            const stripeGateway = gateways.find(
              (g: { gateway?: string }) => String(g?.gateway || "").toLowerCase() === "stripe"
            );
            publishableKey = String(stripeGateway?.publishable_key || "");
          } catch {
            publishableKey = "";
          }
        }
      }
      if (!publishableKey) {
        setPaymentInitError(
          "Stripe is not configured for this supplier. Wait for the order to finish loading and try again."
        );
        return;
      }

      if (!stripePromise) {
        setStripePromise(loadStripe(publishableKey));
      }

      if (queryClientSecret) {
        setClientSecret(queryClientSecret);
        return;
      }

      const intentRes = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${encodeURIComponent(stripeGatewayUserId)}?token=${encodeURIComponent(
          effectiveAccessToken
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: Math.round(Number(displayPaymentRemaining || 0) * 100),
            currency: "usd",
            order_id: paymentIntentMeta.order_id,
            order_number: paymentIntentMeta.order_number,
            customer_name: paymentIntentMeta.customer_name,
            customer_email: paymentIntentMeta.customer_email,
            customer_phone: paymentIntentMeta.customer_phone,
            payment_method_types: ["card"],
          }),
        }
      );
      const intentJson = await intentRes.json().catch(() => ({}));
      const cs = intentJson?.clientSecret || intentJson?.client_secret || "";
      if (cs) {
        setClientSecret(String(cs));
      } else {
        setPaymentInitError("Could not create a payment session. Please try again.");
      }
    } finally {
      setLoadingPaymentUi(false);
    }
  }, [
    effectiveAccessToken,
    stripePromise,
    clientSecret,
    stripeGatewayUserId,
    queryClientSecret,
    queryPublishableKey,
    displayPaymentRemaining,
    paymentIntentMeta,
  ]);

  useEffect(() => {
    if (!showPayModal) autoStripeRetryRef.current = false;
  }, [showPayModal]);

  useEffect(() => {
    if (!showPayModal || !queryClientSecret) return;
    if (stripePromise && clientSecret) return;
    if (autoStripeRetryRef.current) return;
    if (cleanParam(query.get("supplier_id"))) return;
    if (stripeGatewayUserId === queryProjectId) return;
    autoStripeRetryRef.current = true;
    void openPaymentModal();
  }, [
    showPayModal,
    queryClientSecret,
    stripeGatewayUserId,
    queryProjectId,
    query,
    stripePromise,
    clientSecret,
    openPaymentModal,
  ]);

  const refetchPublicOrder = useCallback(async () => {
    if (!queryOrderId || !paymentTriggerToken) return;
    try {
      const normalized = await fetchNormalizedPublicOrderForSummary(
        isRemovalKind,
        isInstallationKind,
        effectiveProjectId || "",
        queryOrderId,
        paymentTriggerToken
      );
      setPublicOrder(normalized);
      onPublicOrderLoaded?.(normalized);
    } catch {
      /* ignore */
    }
  }, [
    queryOrderId,
    paymentTriggerToken,
    isRemovalKind,
    isInstallationKind,
    effectiveProjectId,
    onPublicOrderLoaded,
  ]);

  if (!shouldShowPublicPayUi) return null;

  const successOrderLabel =
    orderLabel || String(orderForPayUi?.order_number || orderForPayUi?.job_number || queryOrderId || "");

  return (
    <>
      <div className="status100-bottom-pay-block no-print">
        <button type="button" className="status100-pay-btn" onClick={() => void openPaymentModal()}>
          {payText}
        </button>
        <div className="status100-pay-note">Secure payments powered by Stripe (256-bit SSL encrypted)</div>
      </div>

      {showPayModal ? (
        <div
          className="no-print"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.6)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => {
            setShowPayModal(false);
            setPaymentInitError(null);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
              padding: 18,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h5 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Enter Card Details</h5>
              <button
                type="button"
                onClick={() => {
                  setShowPayModal(false);
                  setPaymentInitError(null);
                }}
                style={{ border: "none", background: "transparent", fontSize: 22, lineHeight: 1 }}
              >
                x
              </button>
            </div>
            {loadingPaymentUi ? (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#6b7280" }}>Loading payment form...</div>
            ) : stripePromise && clientSecret ? (
              <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                {isRemovalKind ? (
                  <RemovalPublicPayments
                    supplierId={stripeGatewayUserId}
                    emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                    totalAmount={Number(displayPaymentRemaining || 0)}
                    dueAmount={0}
                    orderId={queryOrderId}
                    projectId={effectiveProjectId}
                    publicToken={effectiveAccessToken}
                    selectedPaymentMethod="card"
                    createdBy={paymentCreatedBy}
                    paidBy={paymentPaidBy}
                    onPaymentSuccess={async () => {
                      setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                      setShowPayModal(false);
                      setPaymentInitError(null);
                      setShowPaymentSuccessModal(true);
                      try {
                        if (queryOrderId && paymentTriggerToken) {
                          await sendRemovalOrderTriggerEmail({
                            triggerName: "Public Signage Payment applied for removal Job",
                            removalOrderId: queryOrderId,
                            token: paymentTriggerToken,
                            includeOrderPdfAttachment: true,
                          });
                        }
                      } catch {
                        /* ignore */
                      }
                      await refetchPublicOrder();
                    }}
                  />
                ) : isInstallationKind ? (
                  <InstallationPublicPayments
                    supplierId={stripeGatewayUserId}
                    emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                    totalAmount={Number(displayPaymentRemaining || 0)}
                    dueAmount={0}
                    orderId={queryOrderId}
                    projectId={effectiveProjectId}
                    publicToken={effectiveAccessToken}
                    selectedPaymentMethod="card"
                    createdBy={paymentCreatedBy}
                    paidBy={paymentPaidBy}
                    onPaymentSuccess={async () => {
                      setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                      setShowPayModal(false);
                      setPaymentInitError(null);
                      setShowPaymentSuccessModal(true);
                      await refetchPublicOrder();
                    }}
                  />
                ) : (
                  <PublicPayments
                    supplierId={stripeGatewayUserId}
                    emailBrandingSupplierId={supplierUserIdForPaymentBranding}
                    totalAmount={Number(displayPaymentRemaining || 0)}
                    dueAmount={0}
                    orderId={queryOrderId}
                    projectId={effectiveProjectId}
                    publicToken={effectiveAccessToken}
                    selectedPaymentMethod="card"
                    createdBy={paymentCreatedBy}
                    paidBy={paymentPaidBy}
                    onPaymentSuccess={async () => {
                      setPaymentSuccessChargedAmount(Number(displayPaymentRemaining || 0));
                      setShowPayModal(false);
                      setPaymentInitError(null);
                      setShowPaymentSuccessModal(true);
                      try {
                        if (queryOrderId && paymentTriggerToken) {
                          await sendSalesOrderTriggerEmail({
                            triggerName: "Public Signage Payment applied for order",
                            orderId: queryOrderId,
                            token: paymentTriggerToken,
                            paymentMethodName: "card",
                            includeOrderPdfAttachment: true,
                          });
                        }
                      } catch {
                        /* ignore */
                      }
                      await refetchPublicOrder();
                    }}
                  />
                )}
              </Elements>
            ) : (
              <div style={{ padding: "24px 0", textAlign: "center", color: "#dc2626" }}>
                {paymentInitError ||
                  "Unable to initialize Stripe payment. Ensure the order has loaded and Stripe is configured for this supplier."}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {showPaymentSuccessModal ? (
        <div
          className="no-print"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17, 24, 39, 0.55)",
            zIndex: 3200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => {
            setShowPaymentSuccessModal(false);
            setPaymentSuccessChargedAmount(null);
          }}
          role="presentation"
        >
          <div
            style={{
              width: "100%",
              maxWidth: 400,
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
              padding: "28px 24px 22px",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="invoice-payment-success-title"
            aria-modal="true"
          >
            <h2 id="invoice-payment-success-title" style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700 }}>
              Payment successful
            </h2>
            <p style={{ margin: "0 0 8px", fontSize: 15, color: "#4b5563" }}>
              Thank you. Your payment for order {successOrderLabel} was received.
            </p>
            <p style={{ margin: "0 0 22px", fontSize: 16, fontWeight: 600, color: "#3b6fe8" }}>
              {money(paymentSuccessChargedAmount ?? displayPaymentRemaining)} paid
            </p>
            <button
              type="button"
              onClick={() => {
                setShowPaymentSuccessModal(false);
                setPaymentSuccessChargedAmount(null);
                window.location.reload();
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                fontSize: 16,
                fontWeight: 600,
                color: "#fff",
                background: "#16a34a",
                border: "none",
                borderRadius: 10,
                cursor: "pointer",
              }}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
